// Tutorial narrator audio engine.
//
// Voice graph:
//   AudioBufferSourceNode
//     → highpass (≈300 Hz) → lowpass (≈3 kHz)   ← radio "walky-talky" band
//     → makeup gain
//     → split: ┬─ dry  → AudioBus.sfxGain
//              └─ convolver (room IR) → wet gain → AudioBus.sfxGain
//
// Built in the AudioBus's listener context so the chain shares the rest
// of the game's audio graph — the user's SFX volume slider scales the
// narrator's voice along with footsteps, hits, and ambient beds. The
// narrator deliberately bypasses the bus's underwater lowpass (it's not
// registered with bus.registerNode) so dialogue stays intelligible
// while the camera dips below water.
//
// The convolver runs the same IR file the rest of the level uses
// (passed in via opts.reverbIrUrl). This makes the narrator sound like
// he's standing in the same space as the gameplay rather than a flat
// dub. Wet level mirrors the map's audio.reverb.wet.
//
// Files are pre-decoded to AudioBuffers via THREE.AudioLoader (same path
// music + SFX use). The previous implementation streamed via
// HTMLAudioElement + createMediaElementSource and gated playback on the
// canplaythrough event — that was silently broken on iOS Safari (preload
// is ignored, canplaythrough often never fires, fresh <audio> elements
// created off the user-gesture stack get autoplay-blocked).
//
// Locale handling: tries the user's locale folder first
// (`/sounds/narration/tutorial/<locale>/<key>.ogg`), falls back to English
// on either a 404 or a media-decode error. Falling back at PLAY time, not
// at construction, means a half-translated locale still works key-by-key.
// Failed fetches are cached so we don't re-hit the network on replays.
//
// Cancellation: cancel(fadeMs) ramps gain to 0 over fadeMs and resolves
// the in-flight play() promise as 'cancelled'. Used to interrupt the
// active line when the player races ahead.

import * as THREE from 'three';
import type { AudioBus } from '../../../../render/GameAudio';

export type PlayOutcome = 'finished' | 'cancelled' | 'error';

export interface NarratorPlayHandle {
  /** The narration key currently playing (e.g. '2_1_sprint'). */
  readonly key: string;
  /** Resolves when playback finishes (or is cancelled or fails). */
  readonly done: Promise<PlayOutcome>;
  /** Smoothly fade gain to 0 over `fadeMs` and stop. Idempotent. */
  cancel(fadeMs?: number): void;
}

export interface NarratorEngineOptions {
  /** Folder under /public/sounds/narration that holds locale subfolders.
   *  Tutorial passes 'tutorial' (so paths are
   *  /sounds/narration/tutorial/<locale>/<key>.ogg). */
  basePath: string;
  /** Active locale (e.g. 'en', 'es'). */
  locale: string;
  /** Fallback locale used when the active one is missing files. */
  fallbackLocale: string;
  /** Listener fires whenever the active subtitle text should change. The
   *  string is empty when nothing is playing. */
  onSubtitleChange: (text: string) => void;
  /** Resolves a subtitle for a given key+locale. Engine calls this with
   *  the locale chosen for AUDIO; if the audio fell back to English the
   *  subtitle resolver should mirror the same fallback. */
  resolveSubtitle: (key: string, locale: string) => string;
  /** Master narrator volume (0–1). Default 1. */
  volume?: number;
  /** Optional IR file used as a convolver to spatialise the narrator's
   *  voice into the same "room" the gameplay sounds occupy. Loaded
   *  asynchronously — narrator stays dry until the IR decodes. */
  reverbIrUrl?: string;
  /** Wet send (0..) for the convolver path. Mirrors the map's
   *  audio.reverb.wet so changing one place changes both. Default 1. */
  reverbWet?: number;
}

export class NarratorEngine {
  private readonly bus: AudioBus;
  private readonly ctx: AudioContext;
  private readonly highpass: BiquadFilterNode;
  private readonly lowpass: BiquadFilterNode;
  private readonly gain: GainNode;
  private readonly loader: THREE.AudioLoader;
  /** url → cached fetch+decode promise. Rejected promises stay cached so
   *  we don't keep re-hitting 404s for missing locale files. */
  private readonly bufferCache = new Map<string, Promise<AudioBuffer>>();
  private current: { handle: NarratorPlayHandle; source: AudioBufferSourceNode } | null = null;
  /** setTimeout id for an in-flight fadeAndStop. Cleared when stopCurrent
   *  takes over so the late callback can't fire onSubtitleChange('') and
   *  clobber the next line's subtitle. */
  private fadeStopTimer: number | null = null;
  private cancelled = new WeakSet<NarratorPlayHandle>();
  private readonly opts: NarratorEngineOptions;
  private readonly baseVolume: number;

  constructor(opts: NarratorEngineOptions, bus: AudioBus) {
    this.opts = opts;
    this.bus = bus;
    this.baseVolume = opts.volume ?? 1.0;
    this.loader = new THREE.AudioLoader();
    // Build the chain in the AudioBus's listener context so we can
    // connect into its sfxGain. Web Audio rejects connections between
    // nodes from different AudioContexts — the previous standalone
    // context was why narrator volume was orthogonal to the user's
    // SFX slider.
    this.ctx = bus.context;

    // 300 Hz highpass + 3 kHz lowpass = a snug ~300–3000 Hz band — same
    // range a hand-held two-way radio passes. Q a bit above default so
    // the corners are crisp instead of woolly.
    this.highpass = this.ctx.createBiquadFilter();
    this.highpass.type = 'highpass';
    this.highpass.frequency.value = 300;
    this.highpass.Q.value = 0.9;

    this.lowpass = this.ctx.createBiquadFilter();
    this.lowpass.type = 'lowpass';
    this.lowpass.frequency.value = 3000;
    this.lowpass.Q.value = 0.9;

    this.gain = this.ctx.createGain();
    // Bandpass loses a chunk of energy. +3 dB makeup keeps the dialogue
    // sitting at roughly the same loudness as the original sample.
    this.gain.gain.value = this.baseVolume * 1.4;

    this.highpass.connect(this.lowpass);
    this.lowpass.connect(this.gain);
    // Dry path runs into the SFX bus — the user's SFX volume slider
    // (and master) scales the narrator's voice. Bypasses the bus's
    // per-source underwater lowpass on purpose: dialogue should remain
    // intelligible regardless of camera position.
    bus.connectRawToSfx(this.gain);

    if (opts.reverbIrUrl) {
      const conv = this.ctx.createConvolver();
      const wet = this.ctx.createGain();
      wet.gain.value = opts.reverbWet ?? 1.0;
      conv.connect(wet);
      bus.connectRawToSfx(wet);
      // Tap the gain stage post-bandpass so the wet path also has the
      // radio EQ baked in (otherwise the room reverb would carry the
      // full-band signal and the dry path the band-limited one — fights
      // the "in this room" illusion).
      this.gain.connect(conv);
      // Async fetch + decode. While loading, narrator is dry-only.
      this.loader.load(
        opts.reverbIrUrl,
        (decoded: AudioBuffer) => { conv.buffer = decoded; },
        undefined,
        (err) => { console.warn('[NarratorEngine] failed to load IR', opts.reverbIrUrl, err); }
      );
    }
  }

  /** Switch the locale used for future play() calls. Any line currently
   *  playing finishes in its original locale (cancelling mid-sentence
   *  would feel jarring); the next play() picks up the new locale's
   *  audio file and subtitle. Called by main.ts when the user changes
   *  language from the in-match settings modal during the tutorial. */
  setLocale(code: string): void {
    this.opts.locale = code;
  }

  /** Start playback of a narration key. Cancels any line currently
   *  playing first (no overlap — only one narrator voice at a time). */
  play(key: string): NarratorPlayHandle {
    this.stopCurrent();

    const handle: { key: string; done: Promise<PlayOutcome>; cancel: (fadeMs?: number) => void } & NarratorPlayHandle = {
      key,
      done: Promise.resolve('finished' as PlayOutcome),
      cancel: () => { /* set below */ }
    };

    handle.done = new Promise<PlayOutcome>((resolve) => {
      // Try active locale first — fall back to the fallback locale on
      // load error.
      const tryLocales = this.opts.locale === this.opts.fallbackLocale
        ? [this.opts.locale]
        : [this.opts.locale, this.opts.fallbackLocale];

      let attempt = 0;
      const startAttempt = (): void => {
        if (attempt >= tryLocales.length) {
          this.opts.onSubtitleChange('');
          resolve('error');
          return;
        }
        const loc = tryLocales[attempt]!;
        const url = `/sounds/narration/${this.opts.basePath}/${loc}/${key}.ogg`;
        this.loadBuffer(url).then(
          (buffer) => {
            // Player cancelled before the buffer finished decoding.
            if (this.cancelled.has(handle)) {
              this.opts.onSubtitleChange('');
              resolve('cancelled');
              return;
            }
            // A newer play() call has taken over while we were decoding
            // — drop this one on the floor.
            if (this.current !== null && this.current.handle !== handle) {
              resolve('cancelled');
              return;
            }
            // The bus's AudioContext is gated on user activation, but at
            // tutorial-start the user has just clicked through the language
            // pick, so this should be unlocked. Safe to call regardless.
            this.bus.resumeIfSuspended();
            const source = this.ctx.createBufferSource();
            source.buffer = buffer;
            source.connect(this.highpass);
            // Restore gain in case a previous cancel() ramped it to 0.
            this.gain.gain.cancelScheduledValues(this.ctx.currentTime);
            this.gain.gain.setValueAtTime(this.baseVolume * 1.4, this.ctx.currentTime);

            this.current = { handle, source };
            // Subtitle uses the user's chosen locale, NOT `loc` — when
            // audio falls back to English (e.g. for he/sw/th, where we
            // ship localized subtitles but no .ogg), the on-screen text
            // must still match the language the player picked.
            this.opts.onSubtitleChange(this.opts.resolveSubtitle(key, this.opts.locale));

            source.onended = () => {
              this.opts.onSubtitleChange('');
              if (this.current?.handle === handle) this.current = null;
              resolve(this.cancelled.has(handle) ? 'cancelled' : 'finished');
            };
            source.start(0);
          },
          () => {
            attempt += 1;
            startAttempt();
          }
        );
      };

      handle.cancel = (fadeMs = 250) => {
        if (this.cancelled.has(handle)) return;
        this.cancelled.add(handle);
        if (this.current?.handle !== handle) return;
        this.fadeAndStop(fadeMs);
      };

      startAttempt();
    });

    return handle;
  }

  /** Stop the currently-playing line (if any) immediately. */
  cancel(fadeMs = 250): void {
    if (this.current) this.current.handle.cancel(fadeMs);
  }

  /** Set the master narrator volume. Useful for the late "ramp to 0"
   *  case where the player has jumped into the pit while the narrator is
   *  still talking. */
  setVolume(v: number): void {
    const target = Math.max(0, Math.min(1, v)) * 1.4;
    this.gain.gain.cancelScheduledValues(this.ctx.currentTime);
    this.gain.gain.setTargetAtTime(target, this.ctx.currentTime, 0.05);
  }

  dispose(): void {
    this.stopCurrent();
    // The AudioContext is owned by the AudioBus / listener — DO NOT
    // close it here. Disconnect our nodes so they're collectable; the
    // shared context survives for the next match's audio graph.
    try { this.gain.disconnect(); } catch { /* noop */ }
    try { this.lowpass.disconnect(); } catch { /* noop */ }
    try { this.highpass.disconnect(); } catch { /* noop */ }
  }

  private loadBuffer(url: string): Promise<AudioBuffer> {
    let p = this.bufferCache.get(url);
    if (p) return p;
    p = new Promise<AudioBuffer>((resolve, reject) => {
      this.loader.load(
        url,
        (buffer: AudioBuffer) => resolve(buffer),
        undefined,
        (err) => reject(err instanceof Error ? err : new Error(String(err)))
      );
    });
    this.bufferCache.set(url, p);
    // Swallow unhandled-rejection noise — the actual handler in play()
    // routes failures into locale fallback.
    p.catch(() => { /* noop */ });
    return p;
  }

  private stopCurrent(): void {
    if (!this.current) return;
    const { source } = this.current;
    // Detach onended first — we don't want stop()'s teardown to resolve
    // the previous handle's done promise as 'finished' when it was
    // actually preempted. Callers that care use cancel() (which routes
    // through fadeAndStop and resolves as 'cancelled').
    source.onended = null;
    try { source.stop(); } catch { /* may not have started yet */ }
    try { source.disconnect(); } catch { /* noop */ }
    if (this.fadeStopTimer !== null) {
      clearTimeout(this.fadeStopTimer);
      this.fadeStopTimer = null;
    }
    this.opts.onSubtitleChange('');
    this.current = null;
  }

  private fadeAndStop(fadeMs: number): void {
    if (!this.current) return;
    const { handle: ownHandle, source } = this.current;
    const t = this.ctx.currentTime;
    const dur = Math.max(0.01, fadeMs / 1000);
    this.gain.gain.cancelScheduledValues(t);
    this.gain.gain.setValueAtTime(this.gain.gain.value, t);
    this.gain.gain.linearRampToValueAtTime(0, t + dur);
    if (this.fadeStopTimer !== null) clearTimeout(this.fadeStopTimer);
    this.fadeStopTimer = window.setTimeout(() => {
      this.fadeStopTimer = null;
      // Only act if WE'RE still the active line. If a new play() has
      // taken over, stopCurrent already handled cleanup.
      if (this.current?.handle !== ownHandle) return;
      // source.onended (set in play()) will fire after stop() and
      // resolve the promise as 'cancelled' (cancelled.has(handle) was
      // set in cancel()). Restore gain for the next line.
      try { source.stop(); } catch { /* noop */ }
      this.gain.gain.cancelScheduledValues(this.ctx.currentTime);
      this.gain.gain.setValueAtTime(this.baseVolume * 1.4, this.ctx.currentTime);
    }, fadeMs);
  }
}
